// src/combobox.js
// Accessible custom dropdown that replaces a native <select> so each option can
// show a round country flag (native <option> can't render images). Implements the
// ARIA combobox + listbox pattern with keyboard support, and exposes a
// select-compatible surface (`value`, `addEventListener('change')`, `setOptions`)
// so the rest of the widget treats it like the original select.

import { flagUrl } from './iso.js';

let uid = 0;

function makeFlag(alpha3, cls) {
  const url = flagUrl(alpha3, 40);
  if (!url) {
    const span = document.createElement('span');
    span.className = `${cls} ${cls}--empty`;
    span.setAttribute('aria-hidden', 'true');
    return span;
  }
  const img = document.createElement('img');
  img.className = cls;
  img.src = url;
  img.srcset = `${flagUrl(alpha3, 80)} 2x`;
  img.alt = '';
  img.loading = 'lazy';
  img.decoding = 'async';
  return img;
}

export class Combobox extends EventTarget {
  constructor({ label, placeholder }) {
    super();
    this._id = `vmc-${++uid}`;
    this._label = label;
    this._placeholder = placeholder;
    this._countries = [];
    this._value = '';
    this._open = false;
    this._active = -1;
    this._typeahead = '';
    this._typeaheadAt = 0;
    this._onDocPointer = (e) => {
      if (!e.composedPath().includes(this.root)) this.close();
    };
    this._build();
  }

  _build() {
    this.root = document.createElement('div');
    this.root.className = 'vm__combo';

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'vm__combo-btn';
    this.button.id = `${this._id}-btn`;
    this.button.setAttribute('role', 'combobox');
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-controls', `${this._id}-list`);
    if (this._label) this.button.setAttribute('aria-label', this._label);

    this.valueEl = document.createElement('span');
    this.valueEl.className = 'vm__combo-value';

    const caret = document.createElement('span');
    caret.className = 'vm__combo-caret';
    caret.setAttribute('aria-hidden', 'true');

    this.button.append(this.valueEl, caret);

    this.listbox = document.createElement('ul');
    this.listbox.className = 'vm__listbox';
    this.listbox.id = `${this._id}-list`;
    this.listbox.setAttribute('role', 'listbox');
    if (this._label) this.listbox.setAttribute('aria-label', this._label);
    this.listbox.hidden = true;

    this.root.append(this.button, this.listbox);

    this.button.addEventListener('click', () => this.toggle());
    this.button.addEventListener('keydown', (e) => this._onKeydown(e));
    this.listbox.addEventListener('click', (e) => {
      const li = e.target.closest('[role="option"]');
      if (li) this._commit(li.dataset.value);
    });

    this._renderValue();
  }

  // select-compatible API ---------------------------------------------------

  get value() {
    return this._value;
  }

  set value(v) {
    this._setValue(v, false);
  }

  // Populates options and applies an optional preselect (no change event fired,
  // mirroring native `select.value = …`).
  setOptions(countries, preselect) {
    this._countries = Array.isArray(countries) ? countries : [];
    this.listbox.textContent = '';
    this._countries.forEach((c, i) => {
      const li = document.createElement('li');
      li.id = `${this._id}-opt-${i}`;
      li.className = 'vm__option';
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.dataset.value = c.alpha3_code;
      li.append(makeFlag(c.alpha3_code, 'vm__flag'));
      const name = document.createElement('span');
      name.className = 'vm__option-label';
      name.textContent = c.name;
      li.append(name);
      this.listbox.append(li);
    });
    if (preselect && this._countries.some((c) => c.alpha3_code === preselect)) {
      this._setValue(preselect, false);
    } else {
      this._setValue('', false);
    }
  }

  // internals ---------------------------------------------------------------

  _country(value) {
    return this._countries.find((c) => c.alpha3_code === value);
  }

  _setValue(value, emit) {
    this._value = value || '';
    this._renderValue();
    this.listbox.querySelectorAll('[role="option"]').forEach((li) => {
      li.setAttribute('aria-selected', li.dataset.value === this._value ? 'true' : 'false');
    });
    if (emit) this.dispatchEvent(new Event('change'));
  }

  _renderValue() {
    this.valueEl.textContent = '';
    const c = this._country(this._value);
    if (c) {
      this.valueEl.append(makeFlag(c.alpha3_code, 'vm__flag'));
      const txt = document.createElement('span');
      txt.className = 'vm__combo-text';
      txt.textContent = c.name;
      this.valueEl.append(txt);
    } else {
      const ph = document.createElement('span');
      ph.className = 'vm__combo-placeholder';
      ph.textContent = this._placeholder || '';
      this.valueEl.append(ph);
    }
  }

  _commit(value) {
    this._setValue(value, true);
    this.close();
    this.button.focus();
  }

  toggle() {
    this._open ? this.close() : this.open();
  }

  open() {
    if (this._open || !this._countries.length) return;
    this._open = true;
    this.listbox.hidden = false;
    this.root.classList.add('is-open');
    this.button.setAttribute('aria-expanded', 'true');
    const selected = this._countries.findIndex((c) => c.alpha3_code === this._value);
    this._setActive(selected >= 0 ? selected : 0);
    document.addEventListener('pointerdown', this._onDocPointer, true);
  }

  close() {
    if (!this._open) return;
    this._open = false;
    this.listbox.hidden = true;
    this.root.classList.remove('is-open');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.removeAttribute('aria-activedescendant');
    this._setActive(-1);
    document.removeEventListener('pointerdown', this._onDocPointer, true);
  }

  _setActive(i) {
    const items = this.listbox.querySelectorAll('[role="option"]');
    items.forEach((li) => li.classList.remove('is-active'));
    this._active = i;
    if (i < 0 || i >= items.length) {
      this.button.removeAttribute('aria-activedescendant');
      return;
    }
    const li = items[i];
    li.classList.add('is-active');
    this.button.setAttribute('aria-activedescendant', li.id);
    li.scrollIntoView({ block: 'nearest' });
  }

  _move(delta) {
    const n = this._countries.length;
    if (!n) return;
    let i = this._active < 0 ? 0 : this._active + delta;
    i = Math.max(0, Math.min(n - 1, i));
    this._setActive(i);
  }

  _onKeydown(e) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this._open ? this._move(1) : this.open();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._open ? this._move(-1) : this.open();
        break;
      case 'Home':
        if (this._open) { e.preventDefault(); this._setActive(0); }
        break;
      case 'End':
        if (this._open) { e.preventDefault(); this._setActive(this._countries.length - 1); }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (this._open && this._active >= 0) {
          this._commit(this._countries[this._active].alpha3_code);
        } else {
          this.open();
        }
        break;
      case 'Escape':
        if (this._open) { e.preventDefault(); this.close(); }
        break;
      case 'Tab':
        this.close();
        break;
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          this._onType(e.key);
        }
    }
  }

  _onType(ch) {
    const now = Date.now ? Date.now() : new Date().getTime();
    this._typeahead = now - this._typeaheadAt > 700 ? ch : this._typeahead + ch;
    this._typeaheadAt = now;
    const q = this._typeahead.toLowerCase();
    const idx = this._countries.findIndex((c) => c.name.toLowerCase().startsWith(q));
    if (idx < 0) return;
    if (!this._open) this.open();
    this._setActive(idx);
  }
}

export function createCombobox(opts) {
  return new Combobox(opts);
}
